import { Row } from 'react-table';
import { useTranslation } from '@frontend/i18n';
import { Button, PopoverMenu, PopoverMenuItem, TableRowActions, usePopoverMenu } from '@frontend/design-system';

type MoreButtonProps<T extends object> = {
  data: T;
  actions?: TableRowActions<T>['actions'];
};
export const MoreButton = <T extends object>({ data, actions: rowActions }: MoreButtonProps<T>) => {
  const { t } = useTranslation('payments');
  const { getTriggerProps, getMenuProps, getItemProps } = usePopoverMenu({ placement: 'bottom-end' });

  const visibleActions = rowActions?.filter((action) => {
    if (action.hide === true) {
      return true;
    }
    if (action.hide) {
      return !action.hide(data);
    }
    return true;
  });

  if (!visibleActions || visibleActions.length === 0) {
    return null;
  }

  if (visibleActions.length === 1 && visibleActions[0]?.Icon) {
    const singleAction = visibleActions[0];

    const label = typeof singleAction.label === 'function' ? singleAction.label(data) : singleAction.label;

    const trackingId =
      typeof singleAction.trackingId === 'function' ? singleAction.trackingId(data) : singleAction.trackingId;

    return (
      <Button
        variant='tertiary'
        size='small'
        // iconName={singleAction.iconName}
        aria-label={typeof label === 'string' ? t(label) : ''}
        onClick={() => singleAction.onClick?.(data, {} as Row<T>)} // second parameter is table row, not used here
        trackingId={trackingId || `more-action-btn-${singleAction.label}`}
        css={{ color: 'black' }}
      >
        {singleAction.Icon && <singleAction.Icon size={16} />}
      </Button>
    );
  }

  return (
    <>
      <Button iconName='more-small' variant='secondary' size='small' aria-label='More actions' {...getTriggerProps()} />
      <PopoverMenu {...getMenuProps()}>
        {visibleActions?.map(({ label, Icon, trackingId, onClick, disabled, hoverLabel }, indx) => {
          const trackingIdString = typeof trackingId === 'function' ? trackingId(data) : trackingId;
          const labelString = typeof label === 'function' ? label(data) : typeof label === 'string' ? t(label) : label;
          return (
            <PopoverMenuItem
              key={indx}
              {...getItemProps({ index: 0, onClick: () => onClick?.(data, {} as Row<T>) })} // second parameter is table row, not used here
              trackingId={trackingIdString ?? `more-action-btn-${label}`}
              disabled={typeof disabled === 'function' ? disabled(data) : disabled}
              hoverLabel={typeof hoverLabel === 'function' ? hoverLabel(data) : hoverLabel}
            >
              {Icon && <Icon size={16} />}
              {labelString}
            </PopoverMenuItem>
          );
        })}
      </PopoverMenu>
    </>
  );
};
